import { useState } from "react";
import { Heart, MessageCircle, Trash2, MoreHorizontal, Send, Newspaper, Package, ExternalLink } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import type { Post, PostComment } from "@/hooks/usePosts";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface PostCardProps {
  post: Post;
  currentUserId: string | null;
  onToggleLike: (postId: string) => void;
  onDelete: (postId: string) => void;
  onFetchComments: (postId: string) => Promise<PostComment[]>;
  onAddComment: (postId: string, content: string) => Promise<void>;
  onDeleteComment: (commentId: string, postId: string) => Promise<void>;
  isAdmin?: boolean;
}

export function PostCard({
  post,
  currentUserId,
  onToggleLike,
  onDelete,
  onFetchComments,
  onAddComment,
  onDeleteComment,
  isAdmin,
}: PostCardProps) {
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<PostComment[]>([]);
  const [commentText, setCommentText] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [liked, setLiked] = useState(post.liked_by_me || false);
  const [likesCount, setLikesCount] = useState(post.likes_count);

  const displayName = post.profile?.username || 
    [post.profile?.first_name, post.profile?.last_name].filter(Boolean).join(" ") || 
    "Utente";

  const handleToggleComments = async () => {
    if (!showComments) {
      setLoadingComments(true);
      const data = await onFetchComments(post.id);
      setComments(data);
      setLoadingComments(false);
    }
    setShowComments(!showComments);
  };

  const handleLike = () => {
    setLiked(!liked);
    setLikesCount(prev => liked ? prev - 1 : prev + 1);
    onToggleLike(post.id);
  };

  const handleSendComment = async () => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    await onAddComment(post.id, commentText);
    setCommentText("");
    const data = await onFetchComments(post.id);
    setComments(data);
    setSendingComment(false);
  };

  const handleDeleteComment = async (commentId: string) => {
    await onDeleteComment(commentId, post.id);
    setComments(prev => prev.filter(c => c.id !== commentId));
  };

  const isOwner = currentUserId === post.user_id;
  const canDelete = isOwner || isAdmin;
  const timeAgo = formatDistanceToNow(new Date(post.created_at), { addSuffix: true, locale: it });
  const hasSharedContent = post.shared_article || post.shared_product;
  const hasImage = post.image_url && post.image_url !== "shared-content";

  return (
    <article className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <button
          className="flex items-center gap-3 min-w-0"
          onClick={() => navigate(`/utente?id=${post.user_id}`)}
        >
          <Avatar className="w-9 h-9 ring-2 ring-primary/20">
            <AvatarImage src={post.profile?.avatar_url || undefined} />
            <AvatarFallback className="text-xs bg-primary/10 text-primary">
              {displayName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="text-left">
            <p className="text-sm font-semibold text-foreground leading-tight">{displayName}</p>
            <p className="text-[11px] text-muted-foreground">{timeAgo}</p>
          </div>
        </button>
        {canDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-muted transition-colors">
                <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDelete(post.id)} className="text-destructive focus:text-destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina post
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Image */}
      {hasImage && (
        <div className="w-full bg-muted">
          <img src={post.image_url} alt={post.caption || "Post"} className="w-full object-cover max-h-[500px]" loading="lazy" />
        </div>
      )}

      {/* Shared Article */}
      {post.shared_article && (
        <button
          onClick={() => navigate("/news")}
          className="w-full border-t border-border bg-muted/30 p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="p-2 rounded-lg bg-primary/10">
            <Newspaper className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase font-semibold text-primary tracking-wider">News</p>
            <p className="text-sm font-semibold text-foreground truncate">{post.shared_article.title}</p>
            <p className="text-xs text-muted-foreground truncate">{post.shared_article.description}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>
      )}

      {/* Shared Product */}
      {post.shared_product && (
        <button
          onClick={() => navigate("/prodotti")}
          className="w-full border-t border-border bg-muted/30 p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
        >
          {post.shared_product.cover_image && (
            <img src={post.shared_product.cover_image} alt="" className="w-12 h-12 rounded-lg object-cover bg-muted" />
          )}
          {!post.shared_product.cover_image && (
            <div className="p-2 rounded-lg bg-primary/10">
              <Package className="w-5 h-5 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase font-semibold text-primary tracking-wider">Prodotto</p>
            <p className="text-sm font-semibold text-foreground truncate">{post.shared_product.name}</p>
            <p className="text-xs text-muted-foreground">€{post.shared_product.base_price}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>
      )}

      {/* Actions */}
      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        <button onClick={handleLike} className="group flex items-center gap-1.5 transition-transform active:scale-90">
          <Heart className={`w-6 h-6 transition-all duration-200 ${liked ? "fill-red-500 text-red-500 scale-110" : "text-foreground group-hover:text-red-400"}`} />
        </button>
        <button onClick={handleToggleComments} className="group flex items-center gap-1.5 transition-transform active:scale-90">
          <MessageCircle className="w-6 h-6 text-foreground group-hover:text-primary transition-colors" />
        </button>
      </div>

      {/* Likes count */}
      <div className="px-4 pb-1">
        <p className="text-sm font-semibold text-foreground">{likesCount} {likesCount === 1 ? "like" : "likes"}</p>
      </div>

      {/* Caption */}
      {post.caption && (
        <div className="px-4 pb-3">
          <p className="text-sm text-foreground">
            <span className="font-semibold mr-1.5 cursor-pointer hover:opacity-70" onClick={() => navigate(`/utente?id=${post.user_id}`)}>{displayName}</span>
            {post.caption}
          </p>
        </div>
      )}

      {/* View comments */}
      {post.comments_count > 0 && !showComments && (
        <button onClick={handleToggleComments} className="px-4 pb-3 text-sm text-muted-foreground hover:text-foreground transition-colors">
          Vedi {post.comments_count === 1 ? "1 commento" : `tutti i ${post.comments_count} commenti`}
        </button>
      )}

      {/* Comments */}
      {showComments && (
        <div className="border-t border-border">
          <div className="max-h-60 overflow-y-auto px-4 py-3 space-y-3">
            {loadingComments ? (
              <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" /></div>
            ) : comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-2">Nessun commento ancora</p>
            ) : (
              comments.map(comment => {
                const commentName = comment.profile?.username || [comment.profile?.first_name, comment.profile?.last_name].filter(Boolean).join(" ") || "Utente";
                return (
                  <div key={comment.id} className="flex items-start gap-2.5 group">
                    <Avatar className="w-7 h-7 mt-0.5">
                      <AvatarImage src={comment.profile?.avatar_url || undefined} />
                      <AvatarFallback className="text-[10px] bg-muted">{commentName.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm"><span className="font-semibold mr-1.5">{commentName}</span>{comment.content}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: it })}</p>
                    </div>
                    {(currentUserId === comment.user_id || isAdmin) && (
                      <button onClick={() => handleDeleteComment(comment.id)} className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-destructive/10 transition-all">
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
            <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Aggiungi un commento..." className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-sm h-9 px-0" onKeyDown={(e) => e.key === "Enter" && handleSendComment()} maxLength={500} />
            <button onClick={handleSendComment} disabled={!commentText.trim() || sendingComment} className="text-primary font-semibold text-sm disabled:opacity-40 transition-opacity hover:opacity-80 active:scale-95">
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
